import {EmptyStateIcons, Icon, Icons, Table, Tabs} from '@silverfort/sf-components';
import {FC, useEffect, useMemo, useRef, useState} from 'react';
import {
    buildTableColumns,
    getStatsCountByTab,
    getTabFromAdType,
    getWidgetsByTab,
    INVENTORY_AD_TABS,
    INVENTORY_TAB_TO_AD_TYPE,
    INVENTORY_TAB_TO_URL
} from './InventoryActiveDirectoryBuilders';
import {EntitiesStats, InventoryEntity} from './types';
import {httpGet} from '../utils';
import {HttpResponse} from '../../api/types/Http';
import {InsightsStatistics} from '../../modules/ispm/api/types/Insights';
import InventoryFilters from './InventoryFilters';
import {useAppDrawer} from '../AppDrawer/AppDrawerProvider';
import InvestigationDrawer from '../InvestigationDrawer/InvestigationDrawer';
import {DIRECTORY_TYPES, HTTP_OPTIONS} from '@silverfort-nodelibs/common/utils/consts';
import {usePricingModulesState} from '../../context/contexts/PricingModuleContext';
import {useLocation} from 'react-router-dom';

const ENTITIES_PAGE_SIZE = 100;

const InventoryActiveDirectory: FC = () => {
    const [pageNumber, setPageNumber] = useState(0);
    const [lastPageReceived, setLastPageReceived] = useState(false);
    const [selectedTab, setSelectedTab] = useState<number>(0);
    const [selectedFilters, setSelectedFilters] = useState<{ [key: string]: string[] | number[] | boolean[] }>({});
    const [searchValue, setSearchValue] = useState('');
    const [sortSettings, setSortSettings] = useState<{ orderBy: keyof InventoryEntity; sortDirection: 'asc' | 'desc' }>(
        {
            orderBy: 'name',
            sortDirection: 'asc'
        }
    );
    const [loading, setLoading] = useState(false);
    const [systemStats, setSystemStats] = useState<InsightsStatistics | undefined>();
    const [entitiesStats, setEntitiesStats] = useState<EntitiesStats | undefined>();
    const [entitiesData, setEntitiesData] = useState<InventoryEntity[]>([]);
    const { closeAppDrawer, displayContent } = useAppDrawer();
    const { pricingModulesState } = usePricingModulesState();
    const inventoryRef = useRef(null);
    const location = useLocation();

    const calcFiltersString = (page: number) => {
        let str = `pageSize=${ENTITIES_PAGE_SIZE}&pageNumber=${page}&orderBy=${sortSettings.orderBy}&sortDirection=${sortSettings.sortDirection}`;

        let filters: Record<string, string | string[] | number[] | boolean[]> = {};

        if (searchValue) {
            filters = { name: searchValue };
        }

        if (Object.keys(selectedFilters)?.length) {
            filters = { ...filters, ...selectedFilters };
        }

        if (Object.keys(filters).length) {
            str = `${str}&filters=${encodeURIComponent(JSON.stringify(filters))}`;
        }

        return str;
    };

    const loadData = async (overrideCurrentData = true) => {
        try {
            setLoading(true);

            const page = overrideCurrentData ? 0 : pageNumber;
            const entitiesRes = (await httpGet(
                `/inventory/entities/${INVENTORY_TAB_TO_URL[selectedTab]}?${calcFiltersString(page)}`
            )) as unknown as HttpResponse<InventoryEntity[]>;

            if (!inventoryRef.current) {
                return;
            }

            if (entitiesRes.data?.length < ENTITIES_PAGE_SIZE) {
                setLastPageReceived(true);
            } else {
                setLastPageReceived(false);
            }

            if (overrideCurrentData) {
                setPageNumber(0);
                setEntitiesData(entitiesRes.data);
            } else {
                setEntitiesData((prev) => [...prev, ...entitiesRes.data]);
            }

            setLoading(false);
        } catch (e) {
            console.error('Failed to load inventory data.', e);
        }
    };

    useEffect(() => {
        const loadStatistics = async () => {
            try {
                if (!systemStats) {
                    const stats = (await httpGet('/insights/statistics', {
                        [HTTP_OPTIONS.REQ_OPTIONS]: {
                            params: {
                                entitySourceType: DIRECTORY_TYPES.ad.key
                            }
                        }
                    })) as unknown as HttpResponse<InsightsStatistics>;
                    setSystemStats(stats.data);
                }

                if (!entitiesStats) {
                    const stats = (await httpGet('/inventory/statistics')) as unknown as HttpResponse<EntitiesStats>;
                    setEntitiesStats(stats.data);
                }
            } catch (e) {
                console.error('Failed to load inventory statistics.', e);
            }
        };

        loadStatistics();
        return closeAppDrawer;
    }, []);

    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        const investigateAdType = urlParams.get('investigateAdType');

        if (investigateAdType) {
            const adType = parseInt(investigateAdType);
            if (!isNaN(adType)) {
                const tabIndex = getTabFromAdType(adType);
                setSelectedTab(tabIndex);
            }
        }
    }, []);

    useEffect(() => {
        !loading && loadData();
    }, [selectedTab, sortSettings, searchValue, selectedFilters]);

    useEffect(() => {
        pageNumber > 0 && loadData(false);
    }, [pageNumber]);

    const widgets = useMemo(() => {
        return getWidgetsByTab(
            selectedTab,
            systemStats || {},
            entitiesStats,
            (filterId: string, filterValue: string | number) =>
                setSelectedFilters({ ...selectedFilters, [filterId]: [filterValue] as number[] | string[] }),
            pricingModulesState
        );
    }, [selectedTab, systemStats, entitiesStats, selectedFilters, pricingModulesState]);

    return (
        <div ref={inventoryRef} className="font-sf h-full p-6 w-full bg-white flex flex-col gap-4 overflow-y-hidden">
            <div className="flex gap-2 h-10">
                <div className="flex items-center justify-center">
                    <Icon icon={Icons.ActiveDirectory} size={40} />
                </div>
                <div className="flex h-full flex-col gap-1">
                    <div className="h-[18px] font-sf-poppins text-15 text-sf-gray-800 font-bold">Active Directory</div>
                    <div className="flex gap-1 h-4 text-sf-gray-500 text-13">
                        <span className="font-bold">{systemStats?.systemStatistics?.domains || 0}</span> domains |{' '}
                        <span className="font-bold">{getStatsCountByTab(selectedTab, systemStats) || 0}</span>
                        {INVENTORY_TAB_TO_URL[selectedTab]}
                    </div>
                </div>
            </div>
            <div className="w-full h-12">
                <Tabs
                    tabs={INVENTORY_AD_TABS}
                    selectedTab={selectedTab}
                    onClick={(tab) => {
                        setSelectedFilters({});
                        setSearchValue('');
                        setSelectedTab(tab);
                        setSortSettings({ orderBy: 'name', sortDirection: 'asc' });
                    }}
                />
            </div>

            <div className="w-full flex gap-2">{widgets}</div>
            <InventoryFilters
                selectedTab={selectedTab}
                domainsList={systemStats?.domains || []}
                selectedFilters={selectedFilters}
                setSelectedFilters={setSelectedFilters}
                searchValue={searchValue}
                setSearchValue={setSearchValue}
            />

            <Table
                columns={buildTableColumns(selectedTab, pricingModulesState)}
                rows={entitiesData || []}
                onSortChange={(column, dir) => {
                    setSortSettings({ orderBy: column, sortDirection: dir });
                }}
                onScrollEnd={() => {
                    !lastPageReceived && !loading && setPageNumber(+pageNumber + 1);
                }}
                defaultSortColumn={sortSettings.orderBy}
                defaultSortDirection={sortSettings.sortDirection}
                loading={loading || !systemStats}
                keyExtractor={(item) => item.guid}
                onRowClick={(item) => {
                    displayContent(
                        <InvestigationDrawer
                            guid={item.guid}
                            adType={INVENTORY_TAB_TO_AD_TYPE[selectedTab]}
                            identifier={item.identifier || item.display_name || item.name}
                        />
                    );
                }}
                bodyHeight={window.innerHeight - 548}
                EmptyStateProps={{
                    className: 'w-full h-[250px] border border-sf-gray-100 rounded-lg py-12',
                    title: 'No Results',
                    description: 'Try refining your search',
                    icon: EmptyStateIcons.NoResults
                }}
            />
        </div>
    );
};

export default InventoryActiveDirectory;
